import axios from 'axios';
import { useEffect, useState } from 'react';

export interface SlugAvailability {
  /** True only when the server has said another guide holds this slug. */
  taken: boolean;
  takenBy?: { _id: string; title?: string; status?: string };
  checking: boolean;
}

/** Long enough that typing a slug is one request, short enough to answer before Save. */
export const SLUG_CHECK_DEBOUNCE_MS = 300;

/**
 * Does another guide already hold this slug? Asked of the server, by slug.
 *
 * Both editors used to answer this from `GET /api/guides`, which returned the first 500 guides,
 * so a collision with any guide after that passed silently and was found by the save. The create
 * screen also exempted imports outright (`!importedGuideId && ...`) because an import's own row
 * would otherwise read as a collision with itself; that exemption meant an imported guide whose
 * slug belonged to a DIFFERENT guide was never warned about either. `excludeId` is the right
 * shape: exclude exactly the row being edited, and check everything else.
 *
 * A failed check reports "not taken". The server refuses a duplicate on save regardless, so a
 * check that cannot run must not be the thing that stops someone saving.
 */
export function useSlugAvailability(slug: string, excludeId?: string | null): SlugAvailability {
  const [state, setState] = useState<SlugAvailability>({ taken: false, checking: false });
  const wanted = slug.trim();

  useEffect(() => {
    if (!wanted) {
      setState({ taken: false, checking: false });
      return undefined;
    }
    let cancelled = false;
    setState((s) => ({ ...s, checking: true }));
    const timer = setTimeout(() => {
      axios
        .get('/api/guides/slug-available', {
          params: { slug: wanted, ...(excludeId ? { excludeId } : {}) },
        })
        .then(({ data }) => {
          if (cancelled) return;
          setState({ taken: data?.available === false, takenBy: data?.takenBy, checking: false });
        })
        .catch(() => {
          if (!cancelled) setState({ taken: false, checking: false });
        });
    }, SLUG_CHECK_DEBOUNCE_MS);
    return () => {
      // A slower answer for an older slug must never overwrite the answer for the current one.
      cancelled = true;
      clearTimeout(timer);
    };
  }, [wanted, excludeId]);

  return state;
}
